import ReactECharts from 'echarts-for-react';
import { type JSX } from 'react';

import { CSSColors } from 'Components/Colors';

export interface TrendLineChartProps {
    /** `xAxisLabel`s should be timestamps that are already formatted. They will be displayed directly to the user. */
    data: { value: number; xAxisLabel: string }[];
}

/**
 * Renders a simple line graph with no displayed axes. Intended to provide a rough visual summary of values changing over time.
 * Hovering over a region of the graph will show a hover with the x-axis label and the value for that point in time.
 */
export const TrendLineChart = (props: TrendLineChartProps): JSX.Element => {
    const ariaDescription = (() => {
        const xAxisLabels = props.data.map((dataPoint) => dataPoint.xAxisLabel);
        const valuesDescriptions = xAxisLabels.map((xAxisLabel, xAxisIndex) => `For ${xAxisLabel}, the value is ${props.data[xAxisIndex].value}.`).join(' ');
        return `A trendline chart showing values over time. ${valuesDescriptions}`;
    })();

    return (
        <ReactECharts
            aria-label={ariaDescription}
            role="img"
            option={{
                tooltip: {
                    trigger: 'axis',
                },
                grid: {
                    top: 10,
                    bottom: 10,
                    left: 0,
                    width: '99%', // If set to 100%, hovering over the rightmost data point fails to show the dotted x-axis line.
                },
                xAxis: [
                    {
                        type: 'category',
                        data: props.data.map((element) => element.xAxisLabel),
                        axisTick: {
                            show: false,
                        },
                        axisLine: {
                            show: false,
                        },
                        axisLabel: {
                            show: false,
                        },
                        boundaryGap: false,
                    },
                ],
                yAxis: [
                    {
                        show: false,
                    },
                ],
                series: [
                    {
                        type: 'line',
                        itemStyle: {
                            color: CSSColors.LIGHT_BLUE,
                        },
                        lineStyle: {
                            width: 5,
                        },
                        data: props.data.map((element) => element.value),
                        symbol: 'none',
                        smooth: true,
                    },
                ],
            }}
            style={{
                height: '100%',
                width: '100%',
            }}
        />
    );
};
